import { fetchUrl } from "../utils/fetch-url.js";
import { types } from "../utils/types.js";
import { bridge } from "./bridge.js";
import { coreEngine } from "./engine.js";
import { env as environment } from "./environment.js";
import { events } from "./events.js";
import { logs } from "./logs.js";
import { parsers } from "./parsers/parsers.js";
import { serve } from "./serve.js";
import { store } from "./store.js";
import { trace } from "./trace.js";

const NODE_ASSETS_FOLDER = ".";
const BROWSER_ASSETS_FOLDER = "assets";

const SERVE_JSON = "serve.json";
const BUNDLE_JSON = "bundle.json";

logs.activateLogs();

const normalize = (path) => {
  // Remove any consecutive slashes and replace backslashes with forward slashes
  return path.replaceAll(/\/+/g, "/").replaceAll("\\", "/");
};

const join = (...paths) => {
  // Normalize each path and join them with a forward slash
  return normalize(paths.join("/"));
};

export default function () {
  const isNode = typeof window === "undefined";
  init();

  console.log("[dmaf] running for", environment.platform, "motion:", environment.motion);

  // start the init process
  async function init() {
    try {
      resolveModules();

      const browserServePath = globalThis.assetsPath
        ? `${globalThis.assetsPath}${SERVE_JSON}`
        : `/${BROWSER_ASSETS_FOLDER}/${SERVE_JSON}`;

      const browserBundlePath = globalThis.assetsPath
        ? `${globalThis.assetsPath}${BUNDLE_JSON}`
        : `/${BROWSER_ASSETS_FOLDER}/${BUNDLE_JSON}`;

      const serveJsonPromise = isNode
        ? nodeGetJSON(join(NODE_ASSETS_FOLDER, SERVE_JSON))
        : fetchUrl.fetchJson(browserServePath);
      const serveJson = await serveJsonPromise;

      const bundleJsonPromise = isNode
        ? nodeGetJSON(join(NODE_ASSETS_FOLDER, BUNDLE_JSON))
        : fetchUrl.fetchJson(browserBundlePath);
      const bundleJson = await bundleJsonPromise;

      // Configure the old logging system
      const logsLevels = bundleJson.logs;
      serve.addServeObject({ ...serveJson.serve, logs: logsLevels });

      parsePlatforms(bundleJson);

      parseWithoutDissociation("descriptors", bundleJson);
      parseWithDissociation("data", bundleJson);
      parseWithDissociation("configs", bundleJson);
      parseWithDissociation("serve", serveJson);

      if (store.get("serve.remoteLogs")) {
        logs.enableRemoteLogging();
      }
      if (store.get("serve.logEvents")) {
        events.activateLogs();
      }

      // Enable trace in development — convention: trace port = dev server port + 5000
      const devPort = store.get("serve.port");
      if (devPort) {
        const tracePort = devPort + 5000;
        const runtime = isNode ? "backend" : "frontend";
        trace.enable(runtime);

        // Connect to monitor via WebSocket
        const wsProtocol = isNode ? "ws:" : (location.protocol === "https:" ? "wss:" : "ws:");
        const wsUrl = isNode
          ? `ws://127.0.0.1:${tracePort}/api/trace/ws`
          : `${wsProtocol}//127.0.0.1:${tracePort}/api/trace/ws`;

        const WebSocketImpl = isNode ? (await import("ws")).default : WebSocket;
        const traceWs = new WebSocketImpl(wsUrl);
        let wsReady = false;
        let wsQueue = [];

        traceWs.addEventListener("open", () => {
          wsReady = true;
          for (const msg of wsQueue) traceWs.send(msg);
          wsQueue = [];
        });
        traceWs.addEventListener("error", () => {});
        traceWs.addEventListener("message", (event) => {
          const data = typeof event.data === "string" ? event.data : event.data.toString();
          if (data === "snapshot") trace.snapshot();
        });

        trace.setTransport((entries) => {
          const msg = JSON.stringify({ entries });
          if (wsReady) {
            traceWs.send(msg);
          } else {
            wsQueue.push(msg);
          }
        });
      }
      
      parseWithDissociation("logs", bundleJson);

      // Connect runtimes declared in serve configuration
      for (const [name, config] of Object.entries(serve.runtimes)) {
        bridge.connect(name, config);
      }

      dispatchInit();
    } catch (error) {
      onError(error);
    }
  }

  function onError(error) {
    console.error(error);
  }

  function dispatchInit() {
    console.log("[dmaf] init complete");
    events.addEventToQueue("dmaf_init");
  }

  // prefilter build based on platforms
  function parsePlatforms(build) {
    if (!build.platforms) {
      return build;
    }

    const environmentPlatform = environment.platform;
    const matchName =
      environmentPlatform && environmentPlatform.includes(environmentPlatform)
        ? environmentPlatform
        : "";

    checkArray(build.configs);
    delete build.platforms;

    return build;

    function checkArray(array) {
      const toRemove = [];

      for (const element of array) {
        checkShouldBeRemoved(element);
      }
      for (const element of toRemove) {
        remove(element);
      }

      function checkShouldBeRemoved(element) {
        if (types.isEntity(element)) {
          if (shouldRemove(element)) {
            toRemove.push(element);
          } else {
            checkObject(element);
          }
        }
      }

      function remove(element) {
        array.splice(array.indexOf(element), 1);
      }
    }

    function checkObject(object) {
      for (const property of Object.entries(object)) {
        checkShouldBeRemoved(property[0], property[1]);
      }

      function checkShouldBeRemoved(key, value) {
        if (types.isArray(value)) {
          checkArray(value);
        } else if (types.isEntity(value) && shouldRemove(value)) {
          delete object[key];
        }
      }
    }

    function shouldRemove(element) {
      const platformMismatch = element.platforms
        ? !element.platforms.includes(matchName)
        : false;
      const motionMismatch = element.motion
        ? element.motion !== environment.motion
        : false;
      delete element.platforms;
      delete element.motion;
      return platformMismatch || motionMismatch;
    }
  }
}

async function nodeGetJSON(url) {
  const { isAbsolute, resolve } = await import("node:path");
  const { readFileSync } = await import("node:fs");

  if (!isAbsolute(url)) {
    url = resolve(process.env["ASSETS_PATH"], url);
  }

  const file = readFileSync(url);
  return JSON.parse(file);
}

function resolveModules() {
  coreEngine.registerImplementations();
}

function parseWithDissociation(key, build) {
  const parser = parsers.getType(key);
  parser(build[key]);
  delete build[key];
  return build;
}

function parseWithoutDissociation(key, build) {
  const parser = parsers.getType(key);
  parser(build);
  return build;
}
